import { Alert, Button, Flex, Form, Input, message, Select, Typography } from "antd";
import { ApiService } from "api/api.service";
import PageTitle from "components/PageTitle";
import EntryLayout from "layout/EntryLayout";
import { useEffect, useState } from "react";
import { LuEye, LuEyeOff } from "react-icons/lu";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getErrorMessage } from "utils/functions";
import OTPForm from "./components/OTPForm";
import { useAppSelector } from "store";
import { storedTokens } from "utils/storedTokens";

type T_FormValues = { email?: string; password?: string; phone?: string; code?: string };

function Login() {
  const [emailLogin, setEmailLogin] = useState(true);
  const [showOTPPage, setShowOTPPage] = useState(false);
  const [phoneDetails, setPhoneDetails] = useState<{ phone?: string; code?: string } | null>(null);

  const user = useAppSelector((state) => state.user);
  const location = useLocation();
  const navigate = useNavigate();
  const token = storedTokens.accessToken.getToken();
  const refreshToken = storedTokens.refreshToken.getToken();
  const from = location.state?.from?.pathname;

  useEffect(() => {
    if (user && token && refreshToken) {
      navigate(from || "/dashboard", { replace: true });
    }
  }, [user, token, refreshToken, from, navigate]);

  const login = ApiService.user.useLogin();
  const phoneLogin = ApiService.user.usePhoneLogin();

  function handleLogin(values: T_FormValues) {
    if (emailLogin) {
      login.mutate({
        email: values?.email ? values?.email?.toLowerCase() : values?.email,
        password: values?.password,
      });
    } else {
      phoneLogin.mutate(
        {
          phone: values?.phone ? values?.phone?.toLowerCase() : values?.phone,
          code: values?.code,
        },
        {
          onSuccess: (_, variables) => {
            setPhoneDetails(variables);
            message.success("OTP has been sent to your number!");
            setShowOTPPage(true);
          },
        },
      );
    }
  }

  function toggleLoginMethod() {
    setShowOTPPage(false);
    setEmailLogin((prev) => !prev);
  }

  return (
    <EntryLayout>
      <PageTitle title="Login" pageTitle={[{ title: "Login" }]} />
      <Flex vertical align="center" className="mb-6">
        <Typography.Title level={3} className="!mb-1">
          Welcome Back
        </Typography.Title>
        <Typography.Text type="secondary">Please enter your details to log in</Typography.Text>
        <Link to="/" onClick={toggleLoginMethod} className="mt-2">
          {emailLogin ? "Login using phone number?" : "Login using email?"}
        </Link>
      </Flex>
      {!showOTPPage ? (
        <Form layout="vertical" onFinish={handleLogin} initialValues={{ code: "+1" }}>
          {emailLogin ? (
            <>
              <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
                <Input />
              </Form.Item>
              <Form.Item name="password" label="Password" rules={[{ required: true }]}>
                <Input.Password iconRender={(visible) => (visible ? <LuEye /> : <LuEyeOff />)} />
              </Form.Item>
            </>
          ) : (
            <Flex gap={8} align="center">
              <Form.Item
                name="code"
                label="Code"
                rules={[{ required: true, message: "Enter code" }]}
                style={{ width: 100 }}
              >
                <Select
                  options={[
                    { value: "+1", label: "+1" },
                    { value: "+91", label: "+91" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="phone"
                label="Phone Number"
                rules={[{ required: true, pattern: /^[0-9]+$/, message: "Enter a valid phone number" }]}
                className="flex-1"
              >
                <Input />
              </Form.Item>
            </Flex>
          )}

          {login.isError && (
            <Alert closable type="error" style={{ marginBottom: 24 }} message={getErrorMessage(login.error)} />
          )}
          {phoneLogin.isError && (
            <Alert closable type="error" style={{ marginBottom: 24 }} message={getErrorMessage(phoneLogin.error)} />
          )}

          <Button block loading={login.isPending || phoneLogin.isPending} type="primary" htmlType="submit">
            Login
          </Button>
        </Form>
      ) : (
        <OTPForm phoneDetails={phoneDetails} />
      )}
    </EntryLayout>
  );
}

export default Login;
